/**
 * Section frames — the shared contract
 * ====================================
 *
 * THIS MODULE IS SHARED WITH THE CUSTOMIZER (packages/3d-handler). It must stay free of
 * admin-portal imports — `three` and nothing else — because a section an operator authors
 * here and a section a customer engraves there have to be the same rectangle on the same
 * surface, and the only way to guarantee that is for both to run this code rather than
 * two copies of it.
 *
 * The maths that preceded it was duplicated verbatim across the two repos: `computeOverlayUV`
 * in admin's applyShaderOnMaterial.ts and in the Customizer's applyDesignToTargetObjectMaterial.js,
 * in separate repos on separate release cadences. A divergence of one term there moves a
 * section silently between authoring and engraving. Do not reintroduce that.
 */

/** Model-space vector, in the product root's local frame. */
export type Vec3 = [number, number, number]

export interface SectionFrame {
  id: string
  name: string
  /** Mesh this section projects onto. One mesh can carry several frames. */
  meshName: string
  /**
   * Centre of the section, in the PRODUCT ROOT's local space, in metres — the same
   * units the GLB is authored in, so no conversion sits between a raycast hit and
   * what gets stored. The inspector shows millimetres.
   */
  origin: Vec3
  /** Unit vector the section faces along. Artwork is projected down -normal. */
  normal: Vec3
  /**
   * Unit vector for the section's +V (its "up" on the printed artwork). Only the part
   * perpendicular to `normal` is used, so this never has to be exactly orthogonal —
   * see orthonormalise() in core/frameMath.
   */
  up: Vec3
  /** Extent across the surface, millimetres. */
  width: number
  height: number
  /**
   * How far the projection reaches through the model, millimetres. Keeps a section on
   * the near wall instead of printing through onto the far side of a hollow part.
   */
  depth: number
  /**
   * Curvature the artwork wraps around, millimetres. 0 is a flat decal. A positive
   * radius bends the projection about an axis that sits `wrapRadius` behind the origin,
   * along the frame's up direction — so a cup's frame with wrapRadius set to its radius
   * reproduces V2's cylindrical wrap exactly, and every value between is a chamfer or a
   * soft shoulder that V2 cannot express at all.
   */
  wrapRadius: number
  /**
   * How far the surface may turn away from the section before it stops being engraved,
   * degrees from the frame's own facing direction.
   *
   * This is what keeps a section the size it says it is. A projector lays its artwork on
   * a surface by casting it along one direction, so a face tilted by θ receives it
   * stretched by 1/cos(θ) — at 80° that is nearly 6×, and a face parallel to the cast
   * direction is stretched without limit. Left unbounded, widening a section past the
   * edge of the face it sits on does not grow it, it smears it down the side wall.
   *
   * Bounding the angle bounds the stretch: the default 55° admits at most 1.7×, so a
   * section keeps its true millimetres across a rounded edge and simply stops where the
   * surface turns away. A wrapped section measures the angle from the curved surface it
   * follows, so raising wrapRadius does not fight this limit.
   */
  maxAngleDeg: number
}

/** Ceiling on frames per mesh — the shader carries a fixed-size uniform block and atlas. */
export const MAX_FRAMES_PER_MESH = 8

/** Atlas tiling. 4 × 2 tiles of TILE_PX each holds MAX_FRAMES_PER_MESH sections. */
export const ATLAS_COLS = 4
export const ATLAS_ROWS = 2
export const TILE_PX = 512

/** Millimetres per model unit. GLB coordinates are metres by definition. */
export const MM_PER_UNIT = 1000

/**
 * Default surface angle limit, degrees. 55° admits at most 1.7x projection stretch at the
 * edge of a section — enough to carry artwork over a rounded corner, far short of the
 * unbounded smear an unlimited projector produces on a side wall.
 */
export const DEFAULT_MAX_ANGLE_DEG = 55
